<script setup>
import { ref } from 'vue';
import StripeGuideSetupTab from './providers/stripe/StripeGuideSetupTab.vue'
import PaddleGuideSetupTab from "./providers/PaddleGuideSetupTab.vue";
import PaddleClassicGuideSetupTab from "./providers/PaddleClassicGuideSetupTab.vue";
import GuideSetupTab from "./components/GuideSetupTab.vue";
import ApiTrackingGuide from "./guides/ApiTrackingGuide.vue";
import JavascriptTrackingGuide from "./guides/JavascriptTrackingGuide.vue";


const props = defineProps({
    programId: {
        type: Number,
        required: true
    },
    integrationsConfig: {
        type: Object,
        required: true
    },
    programType: {
        type: String,
        default: 'affiliate'
    }
});


const mainTab = ref('stripe');
const manualSlotTabs = [
    { slotName: 'api', tabTitle: 'API' },
    { slotName: 'javascript', tabTitle: 'JavaScript' },
];
</script>
<template>
    <div class="space-y-6">

        <!-- Main Tabs -->
        <tabs>
            <tab @click="mainTab = 'stripe'" :class="{ 'border-b-4': mainTab === 'stripe' }">Stripe</tab>
            <tab @click="mainTab = 'paddle'" :class="{ 'border-b-4': mainTab === 'paddle' }" v-if="programType === 'affiliate'">Paddle</tab>
            <tab @click="mainTab = 'paddle-classic'" :class="{ 'border-b-4': mainTab === 'paddle-classic' }" v-if="programType === 'affiliate'">Paddle Classic</tab>
            <tab @click="mainTab = 'manual'" :class="{ 'border-b-4': mainTab === 'manual' }">Manual</tab>
            <tab @click="mainTab = 'shopify'" :class="{ 'border-b-4': mainTab === 'shopify' }">Shopify</tab>
            <tab @click="mainTab = 'woocommerce'" :class="{ 'border-b-4': mainTab === 'woocommerce' }">WooCommerce</tab>
<!--            <tab @click="mainTab = 'other'" :class="{ 'border-b-4': mainTab === 'other' }">Other</tab>-->
        </tabs>

        <!-- Stripe Section -->
         <stripe-guide-setup-tab v-show="mainTab === 'stripe'"
            :program-id="Number(programId)"
            :config="integrationsConfig.stripe"
            :program-type="programType"
         />

        <!-- Paddle Section -->
        <paddle-guide-setup-tab v-show="mainTab === 'paddle'"
            :program-id="Number(programId)"
            :config="integrationsConfig.paddle"
            :program-type="programType"
        />

        <paddle-classic-guide-setup-tab v-show="mainTab === 'paddle-classic'"
                                :program-id="Number(programId)"
                                :config="integrationsConfig.paddle_classic"
                                :program-type="programType"
        />

        <guide-setup-tab v-show="mainTab === 'manual'"
                        :slot-tabs="manualSlotTabs"
        >
            <template #api>
                <api-tracking-guide :program-type="programType" />
            </template>

            <template #javascript>
                <javascript-tracking-guide />
            </template>

        </guide-setup-tab>

        <!-- Shopify Section -->
        <div v-show="mainTab === 'shopify'" class="space-y-6">

            <div class="space-y-2">
                <h3 class="text-md font-semibold">
                    1. Install <a href="https://apps.shopify.com/partnero-affiliate-management" target="_blank" class="underline hover:underline">Partnero Affiliate Management</a> app from Shopify App Store
                </h3>
            </div>
            <div class="space-y-2">
                <h3 class="text-md font-semibold">
                    2. Connect Partnero account with your Shopify store
                </h3>
                <div class="space-y-1">
                    <p class="text-sm">
                        After installing the Partnero Affiliate Management app on your Shopify store, return to your Shopify account and select <span class="font-semibold">Apps</span>. Click on <span class="font-semibold">Partnero Affiliate Management</span> to be taken to Partnero. Log in and select the affiliate program you wish to link to your Shopify store.
                    </p>
                </div>
            </div>

        </div>

        <!-- WooCommerce Section -->
        <div v-show="mainTab === 'woocommerce'" class="space-y-6">

            <div class="space-y-2">
                <h3 class="text-md font-semibold">
                    1. Download the <a href="https://wordpress.org/plugins/partnero/" target="_blank" class="underline hover:no-underline">Partnero Affiliate Management</a> plugin
                </h3>
                <div class="space-y-1">
                    <p class="text-sm">
                        After downloading the plugin, upload the plugin files to the /wp-content/plugins/partnero directory on your site.
                    </p>
                    <p class="text-sm">
                        Or, to install the plugin directly from your WordPress dashboard, go to <span class="font-semibold">Plugins</span> -> <span class="font-semibold">Add New</span>, search for <span class="font-semibold">Partnero</span>, and click <span class="font-semibold">Install</span>.
                    </p>
                </div>
            </div>
            <div class="space-y-2">
                <h3 class="text-md font-semibold">
                    2. Activate Partnero Affiliate Management plugin
                </h3>
                <div class="space-y-1">
                    <p class="text-sm">
                        Navigate to <span class="font-semibold">Plugins</span> on your WordPress dashboard, then to <span class="font-semibold">Installed plugins</span> and find <span class="font-semibold">Partnero</span>. Click <span class="font-semibold">Activate</span> to enable the plugin.
                    </p>
                </div>
            </div>
            <div class="space-y-2">
                <h3 class="text-md font-semibold">
                    3. Connect Partnero account with your WooCommerce store
                </h3>
                <div class="space-y-1">
                    <p class="text-sm">
                        Go to <span class="font-semibold">Partnero</span> on your WordPress sidebar and enter the API key for your affiliate program to connect. Aan API key can be created <a href="../integrations" target="_blank" class="underline hover:no-underline">here</a>.
                    </p>
                </div>
            </div>

        </div>

        <!-- Other Section -->
        <div v-show="mainTab === 'other'" class="space-y-4">
            <div class="text-sm mb-6 space-y-6">
                <p>Partnero directly integrates with <span class="font-semibold">Shopify</span> and <span class="font-semibold">WooCommerce</span>. For other e-commerce platforms, you can connect using the <a href="https://developers.partnero.com/guide/affiliate.html" target="_blank" class="underline hover:no-underline">Partnero API</a>.</p>

                <div class="flex items-center gap-4 bg-tahiti-100 p-6 rounded border border-gray-900 border-opacity-5">
                    <div class="bg-teal-300 bg-opacity-25 rounded p-4">
                        <icon name="question-line" size="20"></icon>
                    </div>
                    <p class="text-md">If you require assistance with integration, <a href="https://www.partnero.com/service-integration" target="_blank" class="underline hover:no-underline">reach out to the Partnero team</a>, and we'll handle the integration process for your e-commerce store.</p>
                </div>

            </div>
        </div>

    </div>
</template>
